import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useMachine } from '@xstate/react';
import { gameFlowMachine } from './gameFlowMachine';
import { DeckStatus, StorySection, PendingDiceRoll, AfterRollResponse, Card, UpgradeOffer } from '../types';
import { SavedSession, isLocalStorageAvailable } from '../utils/sessionStorage';
import { log } from '../utils/logger';
import { detectVariant, Variant } from '../utils/variant';

interface GameFlowContextType {
  // State values
  sessionId: string;
  deckStatus: DeckStatus | null;
  storySections: StorySection[];
  showCardOfferModal: boolean;
  showStoryContent: boolean;
  storyFadeIn: boolean;
  showContinueButton: boolean;
  showCards: boolean;
  isInitializing: boolean;
  cardOffer: any;
  isAwaitingResponse: boolean;
  gameMode: 'tutorial' | 'normal';
  isFullScreenCardMode: boolean;

  // Tutorial bubble state
  tutorialBubble: any;
  tutorialContext: { backendStep: number } | null;

  // Survey modal state
  showSurveyModal: boolean;
  surveyOrigin: 'demo-end' | 'feedback-button' | null;
  demoEndAnswers: {
    wantToContinue: boolean;
    wantToGiveFeedback: boolean;
  } | null;

  // Dice flow state
  pendingDiceRoll: PendingDiceRoll | null;
  showDicePanel: boolean;
  diceAnimationComplete: boolean;
  afterRollData: AfterRollResponse | null;
  afterRollError: string | null;

  // State checks
  isInDiceFlow: boolean;
  isShowingFirstParagraph: boolean;
  isRollingDice: boolean;
  isAwaitingBranchSections: boolean;
  isError: boolean;
  error: string | null;
  canRetry: boolean;

  // Actions
  handleBackendResponse: (deckStatus: DeckStatus, storySections: StorySection[], backendStep?: number, tutorialCompleted?: boolean) => void;
  handleDeckUpdated: (deckStatus: DeckStatus) => void;
  handleCardsAccepted: (selectedCards: string[]) => void;
  handleCardsSkipped: () => void;
  handleCardActionSubmitted: (cardName?: string, actionPhrase?: string) => void;
  handleContinueStory: () => void;

  // Tutorial actions
  handleTutorialContinue: () => void;
  handleTutorialDismiss: () => void;
  handleTypingComplete: () => void;

  // Card play mode change handler
  handleCardPlayModeChange: (isInCardPlayMode: boolean) => void;

  // Survey modal actions
  handleOpenSurvey: (origin: 'demo-end' | 'feedback-button', demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean }) => void;
  handleCloseSurvey: () => void;

  // Dice flow actions
  handleLetsRollClicked: () => void;
  handleDiceAnimationComplete: () => void;

  // Error actions
  handleDismissError: () => void;
  handleBackendError: (error: string) => void;
  handleRetryAction: () => void;

  // Full-screen card mode actions
  handleExitCardMode: () => void;
  handleEnterCardMode: () => void;

  // Upgrade system state
  pendingUpgrade: UpgradeOffer | null;
  playedCard: Card | null;
  showUpgradeModal: boolean;
  isAnimatingAsterisk: boolean;

  // Turn limit state
  turnsRemaining: number | null;
  turnLimit: number | null;

  // Upgrade system actions
  handleUpgradeSelected: (selectedCardName: string) => void;

  // Landing page state
  showLandingPage: boolean;
  landingSavedSessions: SavedSession[];
  storageAvailable: boolean;
  handleStartNewGame: () => void;
  handleResumeGame: (sessionId: string) => void;
  handleReplaceOldestAndStart: () => void;

  // Audio consent gate
  showAudioConsent: boolean;
  handleAudioConsentGiven: () => void;

  // Experiment variant
  variant: Variant;
}

const GameFlowContext = createContext<GameFlowContextType | null>(null);

export const useGameFlow = () => {
  const context = useContext(GameFlowContext);
  if (!context) {
    throw new Error('useGameFlow must be used within a GameFlowProvider');
  }
  return context;
};

interface GameFlowProviderProps {
  children: ReactNode;
}

export const GameFlowProvider: React.FC<GameFlowProviderProps> = ({ children }) => {
  // Get existing session ID from URL
  const urlParams = new URLSearchParams(window.location.search);
  const existingSessionId = urlParams.get('session_id');
  
  const variant = detectVariant();

  const [state, send] = useMachine(gameFlowMachine, {
    input: { existingSessionId, variant }
  });

  // Log state transitions (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      log.debug('Game Flow State:', state.value);
      log.debug('Game Flow Context:', state.context);
      const contextDebugInfo = {
        gameMode: state.context.gameMode,
        backendStep: state.context.backendStep,
        storySections: state.context.storySections?.length || 0,
        showStoryContent: state.context.showStoryContent,
        showCards: state.context.showCards,
        tutorialBubble: state.context.tutorialBubble ? {
          show: state.context.tutorialBubble.show,
          message: state.context.tutorialBubble.message,
          targetElement: state.context.tutorialBubble.targetElement
        } : null
      };
      log.debug('Game Flow Context SERIALIZED:', JSON.stringify(contextDebugInfo, null, 2));
      
      // Expose to window for debugging
      (window as any).__GAME_DEBUG__ = contextDebugInfo;
    }
  }, [state]);
  
  // Actions
  const handleBackendResponse = (deckStatus: DeckStatus, storySections: StorySection[], backendStep?: number, tutorialCompleted?: boolean) => {
    send({
      type: 'BACKEND_RESPONSE',
      deckStatus,
      storySections,
      backendStep,
      tutorialCompleted
    });
  };

  const handleDeckUpdated = (deckStatus: DeckStatus) => {
    send({ type: 'DECK_UPDATED', deckStatus });
  };

  const handleCardsAccepted = (selectedCards: string[]) => {
    send({ type: 'CARDS_ACCEPTED', selectedCards });
  };
  
  const handleCardsSkipped = () => {
    send({ type: 'CARDS_SKIPPED' });
  };
  
  const handleCardActionSubmitted = (cardName?: string, actionPhrase?: string) => {
    send({ type: 'CARD_ACTION_SUBMITTED', cardName: cardName || '', actionPhrase: actionPhrase || '' });
  };
  
  // Tutorial actions (now handled by game flow machine)
  const handleTutorialContinue = () => {
    send({ type: 'TUTORIAL_CONTINUE' });
  };
  
  const handleTutorialDismiss = () => {
    send({ type: 'TUTORIAL_DISMISS' });
  };
  
  const handleContinueStory = () => {
    send({ type: 'CONTINUE_STORY' });
  };

  const handleCardPlayModeChange = (isInCardPlayMode: boolean) => {
    send({ type: 'CARD_PLAY_MODE_CHANGED', isInCardPlayMode });
  };

  const handleOpenSurvey = (origin: 'demo-end' | 'feedback-button', demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean }) => {
    send({ type: 'OPEN_SURVEY', origin, demoEndAnswers });
  };

  const handleCloseSurvey = () => {
    send({ type: 'CLOSE_SURVEY' });
  };

  // Dice flow handlers
  const handleLetsRollClicked = () => {
    send({ type: 'LETS_ROLL_CLICKED' });
  };

  const handleDiceAnimationComplete = () => {
    send({ type: 'DICE_ANIMATION_COMPLETE' });
  };

  const handleTypingComplete = () => {
    send({ type: 'TYPING_COMPLETE' });
  };

  const handleDismissError = () => {
    send({ type: 'DISMISS_ERROR' });
  };

  const handleBackendError = (error: string) => {
    send({ type: 'BACKEND_ERROR', error });
  };

  const handleRetryAction = () => {
    send({ type: 'RETRY_ACTION' });
  };

  const handleExitCardMode = () => {
    send({ type: 'EXIT_CARD_MODE' });
  };

  const handleEnterCardMode = () => {
    send({ type: 'ENTER_CARD_MODE' });
  };

  const handleUpgradeSelected = (selectedCardName: string) => {
    send({ type: 'UPGRADE_SELECTED', selectedCardName });
  };

  const handleStartNewGame = () => {
    send({ type: 'START_NEW_GAME' });
  };

  const handleResumeGame = (sessionId: string) => {
    send({ type: 'RESUME_GAME', sessionId });
  };

  const handleReplaceOldestAndStart = () => {
    send({ type: 'REPLACE_OLDEST_AND_START' });
  };

  const handleAudioConsentGiven = () => {
    send({ type: 'AUDIO_CONSENT_GIVEN' });
  };

  const contextValue: GameFlowContextType = {
    // State values
    sessionId: state.context.sessionId,
    deckStatus: state.context.deckStatus,
    storySections: state.context.storySections,
    showCardOfferModal: state.context.showCardOfferModal,
    showStoryContent: state.context.showStoryContent,
    storyFadeIn: state.context.storyFadeIn,
    showContinueButton: state.context.showContinueButton,
    showCards: state.context.showCards,
    isInitializing: state.matches('initializing'),
    cardOffer: state.context.cardOffer,
    isAwaitingResponse: state.matches('awaitingResponse'),
    gameMode: state.context.gameMode,
    isFullScreenCardMode: state.context.isFullScreenCardMode,

    // Tutorial bubble state (now from game flow machine)
    tutorialBubble: state.context.tutorialBubble,
    tutorialContext: state.context.gameMode === 'tutorial' ? { backendStep: state.context.backendStep } : null,

    // Survey modal state
    showSurveyModal: state.context.showSurveyModal,
    surveyOrigin: state.context.surveyOrigin,
    demoEndAnswers: state.context.demoEndAnswers,

    // Dice flow state
    pendingDiceRoll: state.context.pendingDiceRoll,
    showDicePanel: state.context.showDicePanel,
    diceAnimationComplete: state.context.diceAnimationComplete,
    afterRollData: state.context.afterRollData,
    afterRollError: state.context.afterRollError,

    // State checks
    isInDiceFlow: state.matches('awaitingInitiateResponse') || state.matches('diceFlow') || state.matches('processingBranchResponse'),
    isShowingFirstParagraph: state.matches({ diceFlow: 'showingFirstParagraph' }),
    isRollingDice: state.matches({ diceFlow: 'rollingDice' }),
    isAwaitingBranchSections: state.matches({ diceFlow: 'awaitingBranchSections' }),
    isError: state.matches('error'),
    error: state.context.error,
    canRetry: state.context.canRetryAction,

    // Actions
    handleBackendResponse,
    handleDeckUpdated,
    handleCardsAccepted,
    handleCardsSkipped,
    handleCardActionSubmitted,
    handleContinueStory,

    // Tutorial actions
    handleTutorialContinue,
    handleTutorialDismiss,

    // Card play mode change
    handleCardPlayModeChange,

    // Survey modal actions
    handleOpenSurvey,
    handleCloseSurvey,

    // Dice flow actions
    handleLetsRollClicked,
    handleDiceAnimationComplete,

    // Tutorial actions (typing)
    handleTypingComplete,

    // Error actions
    handleDismissError,
    handleBackendError,
    handleRetryAction,

    // Full-screen card mode actions
    handleExitCardMode,
    handleEnterCardMode,

    // Upgrade system state
    pendingUpgrade: state.context.pendingUpgrade,
    playedCard: state.context.playedCard,
    showUpgradeModal: state.context.showUpgradeModal,
    isAnimatingAsterisk: state.context.isAnimatingAsterisk,

    // Turn limit state
    turnsRemaining: state.context.turnsRemaining,
    turnLimit: state.context.turnLimit,

    // Upgrade system actions
    handleUpgradeSelected,

    // Landing page state
    showLandingPage: state.context.showLandingPage,
    landingSavedSessions: state.context.landingSavedSessions,
    storageAvailable: isLocalStorageAvailable(),
    handleStartNewGame,
    handleResumeGame,
    handleReplaceOldestAndStart,

    showAudioConsent: state.context.showAudioConsent,
    handleAudioConsentGiven,

    variant,
  };
  
  return (
    <GameFlowContext.Provider value={contextValue}>
      {children}
    </GameFlowContext.Provider>
  );
};